/**
 * Permissions Configuration
 *
 * Defines role-based permissions for mentor/client feature.
 * This provides a single source of truth for access control.
 */

export const PERMISSIONS = {
  mentor: {
    canCreatePlans: true,
    canDeletePlans: true,
    canEditPlans: true,
    canViewPlans: true,
    canManageInventory: true,
    canManageClients: true,
    canAccessAIChat: true,
    canAccessKnowledgeBase: "full",
    canAccessActionable: true,
    canAccessDemos: true,
  },
  client: {
    canViewPlans: true,
    canEditPlans: true, // Full access except delete
    canDeletePlans: false, // MENTOR-ONLY PRIVILEGE
    canCreatePlans: false, // Cannot create NEW plans
    canManageInventory: true,
    canAccessAIChat: true, // CAN use AI Chat Expansion
    canAccessKnowledgeBase: "curated", // Limited subset
    canAccessActionable: false, // Cannot access Smart Credit Planning
    canAccessDemos: false, // Cannot access demos
    canManageClients: false,
  },
  regularUser: {
    // Standard user (Basic/Pro/Elite) permissions
    canCreatePlans: true,
    canDeletePlans: true,
    canEditPlans: true,
    canViewPlans: true,
    canManageInventory: true,
    canAccessAIChat: true,
    canAccessKnowledgeBase: "full",
    canAccessActionable: true,
    canAccessDemos: true,
    canManageClients: false,
  },
};

/**
 * Navigation menu restrictions for each role
 * Used by Layout.js to filter navigation items
 */
export const CLIENT_VIEW_ALLOWED_PATHS = [
  "/history", // Stored Plans (Read/Edit)
  "/creditplan", // View/Edit individual plans
  "/credit-cards-inventory", // Full access
];

/**
 * Get permissions for a given role
 * @param {string} role - Role name (mentor, client, regularUser)
 * @returns {Object} Permissions object
 */
export function getPermissions(role) {
  return PERMISSIONS[role] || PERMISSIONS.regularUser;
}

/**
 * Check if a role has permission
 * @param {string} role - Role name
 * @param {string} permission - Permission name
 * @returns {boolean|string} Permission value (can be boolean or string)
 */
export function hasPermission(role, permission) {
  const permissions = getPermissions(role);
  return permissions[permission] !== undefined
    ? permissions[permission]
    : false;
}

/**
 * Check if a path is allowed for client view
 * @param {string} path - Navigation path
 * @returns {boolean} True if path is allowed
 */
export function isPathAllowedForClient(path) {
  return CLIENT_VIEW_ALLOWED_PATHS.some(
    (allowedPath) => path === allowedPath || path.startsWith(allowedPath + "/")
  );
}

export default PERMISSIONS;
